import { DeepPartial, DeepReadonly } from 'ts-essentials';
import { ICTTextBody, ICTTextParagraph, DeltaOf } from '@tencent/slide-sdk-model';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { TextBoxConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/text/text-box/text-box-converter';
import { TextParagraphConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/text/text-paragraph/text-paragraph-converter';
import { IParagraph, ITextRun } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-para.interface';
import { IParagraphStyle, ITextRunStyle } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-styles.interface';

/**
 * 文本
 */
export class TextConverter {
    /**
     * 文本的sdk -> ui 映射
     * @param sdkData
     * @param uiData
     */
    public static convertPartialDataFromSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextBody>>,
        uiData: IShapeModel,
    ): void {
        if (sdkData.bodyPr) {
            // 文本框属性转换
            uiData.textBodyStyle = uiData.textBodyStyle || {};
            TextBoxConverter.convertPartialDataFromSdkToUi(sdkData.bodyPr, uiData.textBodyStyle);
        }
        if (sdkData.p?.length) {
            // 段落属性转换（内部包含文字属性转换）
            const uiParagraphList: IParagraph[] = [];
            sdkData.p.forEach((sdkParagraph) => {
                if (sdkParagraph) {
                    uiParagraphList.push(TextParagraphConverter.convertPartialDataFromSdkToUi(sdkParagraph));
                }
            });
            uiData.paragraphList = uiParagraphList;
        }
    }

    /**
     * 文本的ui-> sdk 映射
     * @param uiData
     * @param sdkData
     */
    public static convertPartialDataFromUiToSdk(
        uiData: DeepReadonly<DeepPartial<IShapeModel>>,
        sdkData: DeltaOf<ICTTextBody>,
        clearInherit = true,
    ): void {
        // 文本框属性转换
        sdkData.bodyPr = sdkData.bodyPr || {};
        TextBoxConverter.convertPartialDataFromUiToSdk(uiData.textBodyStyle || {}, sdkData.bodyPr);
        if (uiData.paragraphList?.length) {
            // 3.0的继承属性清除逻辑不在convertFromDocElement中了，而是放在提交sdk前
            // 因为在提交sdk前有多次调用convertFromDocElement（清掉了继承属性）
            // 2.0可以通过上层的styleList恢复继承属性，从而保证了页面显示的正常，但3.0没有styleList了
            // 所以需要保证继承属性的清除逻辑只在提交sdk时
            // eslint-disable-next-line max-len
            const clearedParagraphList = TextConverter.clearParagraphListRawSignProps(uiData.paragraphList, clearInherit);
            // 段落属性转换（内部包含文字属性转换）
            const sdkParagraphList: ICTTextParagraph[] = [];
            clearedParagraphList.forEach((uiParagraph) => {
                if (uiParagraph) {
                    sdkParagraphList.push(TextParagraphConverter.convertPartialDataFromUiToSdk(uiParagraph));
                }
            });
            sdkData.p = sdkParagraphList;
        } else if (uiData.paragraphList) {
            sdkData.p = [];
        }
    }

    /**
     * 通过sdk原始数据与继承后的对比，将自有的属性打标
     * 用来做数据提交时剔除继承属性
     * @param ownSdkData
     * @param inheritUiData
     */
    public static sdkToUiSignRawTextProps(
        ownSdkData?: DeepPartial<ICTTextBody>,
        inheritUiData?: IShapeModel,
    ): void {
        if (!ownSdkData || !inheritUiData) {
            return;
        }
        // (1) 转换一个不含文字继承属性的uiData
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const ownUiData = {} as IShapeModel;
        TextConverter.convertPartialDataFromSdkToUi(ownSdkData, ownUiData);
        // (2) 在包含继承的最终uiData上，通过与原始的对比标记出来哪些属性是自身的（用作文本更新时剔除继承属性用，因为现在的文本不是差量更新的）
        if (ownUiData.paragraphList && inheritUiData.paragraphList) {
            TextConverter.addParagraphListRawSignProps(ownUiData.paragraphList, inheritUiData.paragraphList);
        }
    }

    /**
     * 通过  自有的ParagraphList vs 继承后的ParagraphList 对比， 给inheritParagraphList加上raw标签
     * @param ownParagraphList
     * @param inheritParagraphList
     */
    public static addParagraphListRawSignProps(
        ownParagraphList: DeepPartial<IParagraph[]>,
        inheritParagraphList: DeepPartial<IParagraph[]>,
    ): void {
        inheritParagraphList.forEach((inheritParagraph, paragraphIndex) => {
            const ownParagraph = ownParagraphList[paragraphIndex];
            // 标记paragraph属性
            const ownParagraphProps = ownParagraph?.paragraphProps;
            const inheritParagraphProps = inheritParagraph?.paragraphProps;
            if (ownParagraphProps && inheritParagraphProps) {
                Object.keys(ownParagraphProps).forEach((ownParagraphProp) => {
                    if (!ownParagraphProp.startsWith('raw-')) {
                        inheritParagraphProps[`raw-${ownParagraphProp}`] = true;
                    }
                });
            }
            // 标记endParaRPr
            const ownEndParaRPr = ownParagraph?.endParaRPr;
            const inheritEndParaRPr = inheritParagraph?.endParaRPr;
            if (ownEndParaRPr && inheritEndParaRPr) {
                Object.keys(ownEndParaRPr).forEach((ownEndParaRPrProp) => {
                    if (!ownEndParaRPrProp.startsWith('raw-')) {
                        inheritEndParaRPr[`raw-${ownEndParaRPrProp}`] = true;
                    }
                });
            }
            // 标记项目符号属性
            const ownParagraphCharacter = ownParagraph?.paragraphCharacter;
            const inheritParagraphCharacter = inheritParagraph?.paragraphCharacter;
            if (ownParagraphCharacter && inheritParagraphCharacter) {
                inheritParagraphCharacter.raw = Object.keys(ownParagraphCharacter);
            }
            const finder = this.createInheritToOwnTextRunFinder(inheritParagraph?.textRuns, ownParagraph?.textRuns);
            // 标记textRun属性
            inheritParagraph?.textRuns?.forEach((inheritTextRun, index) => {
                const ownTextRun = finder(inheritTextRun as ITextRun, index);
                if (ownTextRun?.styles && inheritTextRun?.styles) {
                    Object.keys(ownTextRun.styles).forEach((ownTextRunProp) => {
                        if (!ownTextRunProp.startsWith('raw-')) {
                            (inheritTextRun.styles!)[`raw-${ownTextRunProp}`] = true;
                        }
                    });
                }
            });
        });
    }

    /**
     * 剔除继承属性，只保留用户自己设置的属性
     * @param paragraphList 段落
     * @param clearInherit 是否在清除raw标志位的同时也清除掉raw标志位没有对应的属性（也就是继承过来的属性）
     */
    public static clearParagraphListRawSignProps(
        paragraphList: DeepReadonly<DeepPartial<IParagraph[]>>,
        clearInherit = true,
    ): IParagraph[] {
        return paragraphList.map((paragraph) => {
            const clearedParagraph: IParagraph = {
                textRuns: [],
                paragraphProps: {},
            };
            // endParaRPr
            if (paragraph?.endParaRPr) {
                const clearedEndParaRPr: ITextRunStyle = {};
                TextConverter.getExcludeSignRawKeys(paragraph.endParaRPr, clearInherit).forEach((prop) => {
                    clearedEndParaRPr[prop] = paragraph.endParaRPr?.[prop];
                });
                if (Object.keys(clearedEndParaRPr).length) {
                    clearedParagraph.endParaRPr = clearedEndParaRPr;
                }
            }

            // textRun
            if (paragraph?.textRuns?.length) {
                const clearedTextRuns: ITextRun[] = [];
                paragraph.textRuns.forEach((textRun: ITextRun) => {
                    const clearedTextRun = { ...textRun };
                    if (textRun?.styles) {
                        clearedTextRun.styles = {};
                        TextConverter.getExcludeSignRawKeys(textRun.styles, clearInherit).forEach((prop) => {
                            (clearedTextRun.styles!)[prop] = textRun.styles?.[prop];
                        });
                    }
                    clearedTextRuns.push(clearedTextRun);
                });
                clearedParagraph.textRuns = clearedTextRuns;
            }

            // character
            let clearedParagraphCharacter = {};
            if (clearInherit) {
                // 清除掉所有raw-标志位 + raw标志位没有对应的属性(继承属性)
                paragraph?.paragraphCharacter?.raw?.forEach((e) => {
                    if (e) {
                        clearedParagraphCharacter[e] = paragraph.paragraphCharacter?.[e];
                    }
                });
            } else {
                // 只清除掉raw-标志位
                clearedParagraphCharacter = { ...(paragraph?.paragraphCharacter || {}) };
                // @ts-ignore
                delete clearedParagraphCharacter.raw;
            }
            if (Object.keys(clearedParagraphCharacter).length) {
                clearedParagraph.paragraphCharacter = clearedParagraphCharacter;
            }

            // paragraphProps
            const clearedParagraphProps: IParagraphStyle = {};
            TextConverter.getExcludeSignRawKeys(paragraph?.paragraphProps || {}, clearInherit).forEach((prop) => {
                // 处理 paddingTop paddingBottom，具体原因见 fixParaStyle 的注释
                if (prop === 'paddingTop') {
                    clearedParagraphProps.paddingTop = paragraph?.paragraphProps?.rawPaddingTop;
                    return;
                }
                if (prop === 'paddingBottom') {
                    clearedParagraphProps.paddingBottom = paragraph?.paragraphProps?.rawPaddingBottom;
                    return;
                }
                clearedParagraphProps[prop] = paragraph?.paragraphProps?.[prop];
            });
            if (Object.keys(clearedParagraphProps).length) {
                clearedParagraph.paragraphProps = clearedParagraphProps;
            }
            return clearedParagraph;
        });
    }
    /**
     * 获取排除掉raw标志位后的有效字段
     * @param obj 对象
     * @param clearInherit 是否在清除raw标志位的同时也清除掉raw标志位没有对应的属性（也就是继承过来的属性）
     * @private
     */
    private static getExcludeSignRawKeys(obj?: { [key: string]: any }, clearInherit = true): string[] {
        let keys;
        if (clearInherit) {
            // 排除r所有raw-标志位 + raw-标志位没有对应的属性(继承属性)
            keys = Object.keys(obj || {}).filter(prop => obj?.[`raw-${prop}`] !== undefined);
        } else {
            // raw-标志位之外的所有属性
            keys = Object.keys(obj || {}).filter(prop => !prop.startsWith('raw-') && obj?.[prop] !== undefined);
        }
        return keys;
    }

    /**
     * 生成 inheritTextRun 到 ownTextRun 的映射关系
     * ownTextRun 中每一项包含一个或多个 inheritTextRun 中的子项
     * @param inheritTextRuns
     * @param ownTextRuns
     * @returns
     */
    private static mapInheritToOwnTextRun(
        inheritTextRuns: DeepPartial<ITextRun[]> = [],
        ownTextRuns: DeepPartial<ITextRun[]> = [],
    ) {
        let ownTextRunIndex = 0;
        let [lastOwnTextRun] = ownTextRuns;
        const mapping = new WeakMap<ITextRun, ITextRun>();
        inheritTextRuns.forEach((currentInheritTextRun: ITextRun) => {
            if (ownTextRunIndex >= ownTextRuns.length) {
                // 此时 ownTextRuns 已经遍历完了，剩下的 inheritTextRun 对应的都是最后一个 ownTextRun
                mapping.set(currentInheritTextRun, lastOwnTextRun as ITextRun);
                return;
            }
            const ownText = ownTextRuns[ownTextRunIndex]?.text || '';
            const inheritText = currentInheritTextRun?.text || '';
            if (ownText.startsWith(inheritText)) {
                // 从当前开始的 inheritTextRun 开始对应当前的 ownTextRun
                lastOwnTextRun = ownTextRuns[ownTextRunIndex];
            }
            if (ownText.endsWith(inheritText)) {
                // 当前 ownTextRun 已经对应完了，开始处理下一个，所以索引 +1
                ownTextRunIndex += 1;
            }
            mapping.set(currentInheritTextRun, lastOwnTextRun as ITextRun);
        });

        return mapping;
    }

    /**
     * 生成一个函数，用于查找 inheritTextRun 对应的 ownTextRun
     * 由于 sdk-ui 的时候可能会进行中英文拆分，所以 inheritTextRuns 和 ownTextRuns 可能不是一一对应的
     * @param inheritTextRuns
     * @param ownTextRuns
     * @returns
     */
    private static createInheritToOwnTextRunFinder(
        inheritTextRuns: DeepPartial<ITextRun[]> = [],
        ownTextRuns: DeepPartial<ITextRun[]> = [],
    ) {
        if (inheritTextRuns.length === ownTextRuns.length) {
            // 两个 run 数组长度相同，代表 inheritTextRuns 和 ownTextRuns 中英文拆分逻辑一致，可以直接使用索引找到对应的 ownTextRun
            return function (inheritTextRun: ITextRun, inheritTextRunIdx: number) {
                return ownTextRuns[inheritTextRunIdx];
            };
        }
        // 否则需要生成一个映射关系
        const map = this.mapInheritToOwnTextRun(inheritTextRuns, ownTextRuns);
        return function (inheritTextRun: ITextRun) {
            return map.get(inheritTextRun);
        };
    }
}
